import { useEffect } from 'react';

import type { Notification } from '@novu/js';
import { Inbox, NovuProvider, Tab, useNovu } from '@novu/react';
import { ArrowRight, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { EnvVars } from '../../../environment';
import { useUserContext } from '@/Contexts/User';

// Inner component that uses useNovu hook within the NovuProvider context
function NovuInboxContent({ subscriberId, tabs }: { subscriberId: string; tabs: Tab[] }) {
  const novu = useNovu();
  const navigate = useNavigate();

  useEffect(() => {
    // Handler for new notifications
    const handleNewNotification = ({ result }: { result: Notification }) => {
      const url = result.primaryAction?.redirect?.url;

      toast.info(
        <div className="flex items-center justify-between gap-2 p-1">
          <div className="flex items-center gap-1">
            <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full">
              <Bell className="h-3 w-3" />
            </div>
            <p className="text-sm leading-tight font-semibold text-gray-900">{result.subject}</p>
          </div>
          <button
            className="flex cursor-pointer items-center gap-1"
            onClick={() => {
              if (url) {
                navigate(url);
              }
            }}
          >
            <span className="text-xs text-blue-600 underline">View</span>
            <ArrowRight className="h-3 w-3 text-blue-600" />
          </button>
        </div>,
        {
          duration: 5000,
        },
      );
    };

    // Subscribe to events
    const unsubscribeNotificationReceived = novu.on('notifications.notification_received', handleNewNotification);

    // Cleanup function
    return () => {
      unsubscribeNotificationReceived();
    };
  }, [novu]);

  return (
    <Inbox
      applicationIdentifier={EnvVars.NOVU_APP_ID}
      subscriberId={subscriberId}
      tabs={tabs}
      socketUrl={EnvVars.NOVU_SOCKET_URL}
      backendUrl={EnvVars.NOVU_BACKEND_URL}
      appearance={{
        variables: {
          borderRadius: '6px',
          colorPrimary: '#31C4AC',
        },
        elements: {
          notificationImage: {
            display: 'none',
          },

          notificationImageLoadingFallback: {
            display: 'none',
          },

          bellContainer: {
            padding: '4px',
            backgroundColor: '#eff6ff', // bg-blue-50 equivalent
            border: '1px solid #bfdbfe', // border-blue-200 equivalent
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%', // rounded-full equivalent
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // shadow-md equivalent
            color: '#2563eb', // text-blue-600 equivalent
          },
        },
      }}
    />
  );
}

export function NovuInbox() {
  const { user } = useUserContext();

  const subscriberId = user?.id ?? '';

  // TODO: Consider if we want to have tabs between different types of message in the future when
  // we have more notification types / events.
  const tabs: Tab[] = [];

  if (!subscriberId) {
    return null;
  }

  return (
    <NovuProvider
      subscriber={subscriberId}
      applicationIdentifier={EnvVars.NOVU_APP_ID}
      socketUrl={EnvVars.NOVU_SOCKET_URL}
      apiUrl={EnvVars.NOVU_BACKEND_URL}
    >
      <NovuInboxContent subscriberId={subscriberId} tabs={tabs} />
    </NovuProvider>
  );
}
